(() => {
  const mq = window.matchMedia('(max-width: 989px)');
  const EASE =
    'transform var(--peak-mobile-sheet-duration) var(--peak-mobile-sheet-ease)';
  const DISMISS_VELOCITY = 0.55;

  const getSearchHost = () => {
    const candidates = [...document.querySelectorAll('peak-search-panel')];
    return candidates.find((host) => host.layer && host.trigger) || null;
  };

  const getMenuState = () => {
    const drawer = document.querySelector('header-drawer');
    if (!drawer) return null;

    const details = drawer.querySelector('#Details-menu-drawer-container');
    const summary = details?.querySelector(':scope > summary');
    const panel = drawer.menuPanel || details?.querySelector(':scope > .menu-drawer');

    if (!details || !summary || !panel) return null;
    return { drawer, details, summary, panel };
  };

  const closeSearch = ({
    restoreFocus = false,
    immediate = false,
    preserveHeader = false,
  } = {}) => {
    const host = getSearchHost();
    if ((host?.opened || host?.closeTimer) && typeof host.close === 'function') {
      host.close({ restoreFocus, immediate, preserveHeader });
    }
  };

  const closeMenu = ({
    immediate = false,
    preserveHeader = false,
  } = {}) => {
    const state = getMenuState();
    if (!state?.details.hasAttribute('open')) return;

    if (typeof state.drawer.closeMenuDrawer === 'function') {
      state.drawer.closeMenuDrawer(new Event('click'), state.summary);
    } else {
      state.summary.click();
    }

    if (immediate) {
      state.details.removeAttribute('open');
      state.summary.setAttribute('aria-expanded', 'false');
    }

    if (preserveHeader) {
      document.querySelector('.section-header')?.classList.add('menu-open');
    }
  };

  const getSheet = (type) => {
    if (type === 'search' || type === 'cart') {
      const host = type === 'cart' ? document.querySelector('peak-cart-panel') : getSearchHost();
      const panel = host?.layer?.querySelector('.peak-search__panel');

      if (!host || !panel) return null;
      return {
        panel,
        input: type === 'search' ? host.input : null,
        /* Match Menu: drag motion belongs to the sheet, not the backdrop. */
        backdrop: null,
        close: () => host.close({ restoreFocus: false }),
      };
    }

    if (type === 'menu') {
      const state = getMenuState();
      if (!state) return null;
      return {
        panel: state.panel,
        backdrop: null,
        close: closeMenu,
      };
    }

    return null;
  };

  /*
    Mutual exclusivity:
    Search and Menu are peers. Opening either closes the other first.
  */
  document.addEventListener('click', (event) => {
    if (!mq.matches) return;

    const searchTrigger = event.target.closest('[data-peak-search-open]');
    if (searchTrigger) {
      closeMenu({ immediate: true, preserveHeader: true });
      return;
    }

    const menuSummary = event.target.closest(
      'header-drawer #Details-menu-drawer-container > summary'
    );

    if (menuSummary) {
      const details = menuSummary.parentElement;
      if (!details.hasAttribute('open')) {
        closeSearch({
          restoreFocus: false,
          immediate: true,
          preserveHeader: true,
        });
      }
    }
  }, true);

  document.addEventListener('peak:cart-opening', () => {
    if (!mq.matches) return;
    closeMenu({ immediate: true });
    closeSearch({ immediate: true });
  });

  // Finish Cart's header/scroll ownership before the next peer opens.
  document.addEventListener('click', (event) => {
    if (!mq.matches || !event.target.closest('[data-peak-search-open], header-drawer #Details-menu-drawer-container > summary')) return;
    document.querySelector('peak-cart-panel')?.close({ immediate: true, restoreFocus: false, preserveHeader: true });
  }, true);

  const states = new WeakMap();

  document.addEventListener('pointerdown', (event) => {
    if (!mq.matches) return;

    const handle = event.target.closest('[data-peak-sheet-handle]');
    if (!handle || event.button > 0) return;

    const type = handle.dataset.peakSheetHandle;
    const sheet = getSheet(type);
    if (!sheet) return;

    const now = performance.now();
    const state = {
      type,
      sheet,
      startY: event.clientY,
      lastY: event.clientY,
      lastTime: now,
      velocity: 0,
      dy: 0,
      dragged: false,
      suppressClick: false,
      focusReleased: false,
      finished: false,
      // Freeze Search's existing distance threshold before keyboard geometry changes.
      dismissThreshold: Math.min(130, Math.max(78, sheet.panel.offsetHeight * 0.15)),
    };

    states.set(handle, state);

    // Keep a handle press from taking focus before the drag commits.
    if (type === 'search' && document.activeElement === sheet.input) event.preventDefault();

    try {
      handle.setPointerCapture(event.pointerId);
    } catch (error) {}

    handle.classList.add('is-dragging');
  });

  document.addEventListener('pointermove', (event) => {
    const handle = event.target.closest('[data-peak-sheet-handle]');
    const state = handle ? states.get(handle) : null;
    if (!state || state.finished || !mq.matches) return;

    const now = performance.now();
    const rawDy = event.clientY - state.startY;
    const dy = Math.max(0, rawDy);
    const dt = Math.max(1, now - state.lastTime);

    state.velocity = Math.max(0, (event.clientY - state.lastY) / dt);
    state.lastY = event.clientY;
    state.lastTime = now;
    state.dy = dy;

    if (dy > 3) {
      state.dragged = true;
      state.suppressClick = true;
    }

    if (!state.dragged) return;

    event.preventDefault();

    state.sheet.panel.style.setProperty('transition', 'none', 'important');
    state.sheet.panel.style.setProperty('transform', `translate3d(0,${dy}px,0)`, 'important');

    if (state.type === 'search' && !state.focusReleased && dy >= state.dismissThreshold) {
      state.focusReleased = true;
      if (document.activeElement === state.sheet.input) state.sheet.input.blur();
    }

    if (state.sheet.backdrop) {
      const progress = Math.min(1, dy / Math.max(1, state.sheet.panel.offsetHeight * 0.7));
      state.sheet.backdrop.style.transition = 'none';
      state.sheet.backdrop.style.opacity = String(1 - progress);
    }
  }, { passive: false });

  const finishPointer = (event) => {
    const handle = event.target.closest('[data-peak-sheet-handle]');
    const state = handle ? states.get(handle) : null;
    if (!state || state.finished) return;
    state.finished = true;

    handle.classList.remove('is-dragging');

    const { panel, backdrop } = state.sheet;
    const threshold = state.type === 'search'
      ? state.dismissThreshold
      : Math.min(130, Math.max(78, panel.offsetHeight * 0.15));
    const shouldDismiss =
      !(state.type === 'search' && event.type === 'pointercancel') &&
      state.dragged &&
      (state.dy >= threshold || state.velocity >= DISMISS_VELOCITY);

    if (shouldDismiss) {
      panel.style.setProperty('transition', EASE, 'important');
      panel.style.setProperty('transform', 'translate3d(0,104%,0)', 'important');

      if (backdrop) {
        backdrop.style.transition = 'opacity .24s ease';
        backdrop.style.opacity = '0';
      }

      window.setTimeout(() => {
        state.sheet.close();

        window.setTimeout(() => {
          panel.style.removeProperty('transition');
          panel.style.removeProperty('transform');
          backdrop?.style.removeProperty('transition');
          backdrop?.style.removeProperty('opacity');
        }, 80);
      }, 220);
    } else if (state.dragged) {
      panel.style.setProperty('transition', EASE, 'important');
      panel.style.setProperty('transform', 'translate3d(0,0,0)', 'important');

      if (backdrop) {
        backdrop.style.transition = 'opacity .24s ease';
        backdrop.style.opacity = '1';
      }

      window.setTimeout(() => {
        panel.style.removeProperty('transition');
        panel.style.removeProperty('transform');
        backdrop?.style.removeProperty('transition');
        backdrop?.style.removeProperty('opacity');
      }, 340);
    }

    try {
      handle.releasePointerCapture(event.pointerId);
    } catch (error) {}

    /*
      Keep the state until click so a drag cannot accidentally become
      a tap-to-close after pointerup.
    */
    window.setTimeout(() => states.delete(handle), 450);
  };

  document.addEventListener('pointerup', finishPointer);
  document.addEventListener('pointercancel', finishPointer);

  document.addEventListener('click', (event) => {
    if (!mq.matches) return;

    const handle = event.target.closest('[data-peak-sheet-handle]');
    if (!handle) return;

    const state = states.get(handle);

    if (state?.suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const sheet = getSheet(handle.dataset.peakSheetHandle);
    sheet?.close();
  });
})();
